/**
 * h5/src/ws/ackTracker.ts
 * ACK 追踪器
 *
 * 行为：
 * - track(msgId)  → 返回 Promise<void>；resolve 表示收到 ack，reject 表示超时
 * - notify(msgId) → 收到 ack 时调用，resolve 对应 Promise
 * - clear()       → 断连时全部 reject（带 'connection closed' reason）
 *
 * 不做自动重发（D 期未实现重发，仅 logger.warn 超时告警）
 */

import { logger } from '@/utils/logger'

/**
 * 单条 ACK 追踪记录
 */
interface AckRecord {
  resolve: () => void
  reject: (err: Error) => void
  timerId: ReturnType<typeof setTimeout>
}

/**
 * ACK 追踪器选项
 */
export interface AckTrackerOptions {
  /** 默认超时（ms），默认 10000 */
  defaultTimeoutMs?: number
  /** 自定义 setTimeout（测试注入） */
  setTimeoutFn?: typeof setTimeout
  /** 自定义 clearTimeout（测试注入） */
  clearTimeoutFn?: typeof clearTimeout
}

/**
 * ACK 追踪器接口
 */
export interface AckTracker {
  /**
   * 开始追踪一条消息的 ack
   * @param msgId      消息 ID
   * @param timeoutMs  超时毫秒（默认 10000）
   * @returns Promise<void> — resolve: 收到 ack；reject: 超时
   */
  track(msgId: string, timeoutMs?: number): Promise<void>

  /**
   * 收到服务端 ack 时调用，resolve 对应 Promise
   * 重复调用同一 msgId 是幂等的
   */
  notify(msgId: string): void

  /**
   * 断连时全部清理，所有 pending Promise reject
   */
  clear(): void

  /**
   * 当前正在追踪的消息数量
   */
  readonly pendingCount: number
}

/**
 * 创建 ACK 追踪器实例
 */
export function createAckTracker(opts?: AckTrackerOptions): AckTracker {
  const defaultTimeoutMs = opts?.defaultTimeoutMs ?? 10_000
  const setTimeoutFn = opts?.setTimeoutFn ?? setTimeout
  const clearTimeoutFn = opts?.clearTimeoutFn ?? clearTimeout

  // msgId → AckRecord 映射
  const _pending = new Map<string, AckRecord>()

  return {
    get pendingCount() {
      return _pending.size
    },

    track(msgId: string, timeoutMs?: number): Promise<void> {
      // 若已在追踪，先清理旧记录（幂等处理）
      const existing = _pending.get(msgId)
      if (existing) {
        clearTimeoutFn(existing.timerId)
        existing.reject(new Error(`[AckTracker] msgId "${msgId}" re-tracked, old promise rejected`))
        _pending.delete(msgId)
      }

      const ms = timeoutMs ?? defaultTimeoutMs

      return new Promise<void>((resolve, reject) => {
        const timerId = setTimeoutFn(() => {
          _pending.delete(msgId)
          logger.warn('[AckTracker] ACK 超时', { msg_id: msgId, timeout_ms: ms })
          reject(new Error(`[AckTracker] ACK timeout for msg_id="${msgId}" after ${ms}ms`))
        }, ms)

        _pending.set(msgId, { resolve, reject, timerId })
      })
    },

    notify(msgId: string): void {
      const record = _pending.get(msgId)
      if (!record) {
        // 重复 notify 幂等，静默忽略
        return
      }
      clearTimeoutFn(record.timerId)
      _pending.delete(msgId)
      record.resolve()
    },

    clear(): void {
      for (const [msgId, record] of _pending) {
        clearTimeoutFn(record.timerId)
        record.reject(new Error(`[AckTracker] Connection closed, ack cancelled for msg_id="${msgId}"`))
      }
      _pending.clear()
    },
  }
}
